import type { TCustomFieldKind } from "@/types/custom-field";
import type { TRequestStatus } from "@/types/request-status";
import type { TRequestWorkflowPendingTransition } from "@/types/request-workflow";
import type { TPriorityTier } from "@/types/request-prioritization";

export type TIntakeLocale = "en" | "fr";

export type TIntakeLocalizedText = Record<TIntakeLocale, string>;

export type TIntakeConditionOp =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "contains"
  | "is_empty"
  | "is_not_empty";

export interface TIntakeConditionClause {
  field: string;
  op: TIntakeConditionOp;
  value?: string | number | boolean | null;
}

export type TIntakeCondition =
  | { all: TIntakeConditionClause[] }
  | { any: TIntakeConditionClause[] }
  | null;

interface TIntakeBaseElement {
  id: string;
  internal?: boolean;
  required?: boolean;
  visible_if?: TIntakeCondition;
  label?: TIntakeLocalizedText;
  tooltip?: TIntakeLocalizedText;
  default_value?: string | number | boolean | string[] | Record<string, boolean> | null;
  bank_field_id?: string;
  /** Stable cross-workspace key of the referenced bank field, resolved live by
   * the backend (resolve-at-read). Lets consumers match a default field by
   * identity instead of its renameable label. */
  bank_field_cross_workspace_key?: string;
}

/** Canonical system_field kinds — must mirror ``SUPPORTED_SYSTEM_FIELDS`` in the backend schema. */
export type TIntakeSystemFieldKind =
  | "request_name"
  | "requester"
  | "request_owner"
  | "request_reviewer"
  | "target_launch_date";

/** Default kinds the validator + builder auto-inject. PR6 dropped ``client_team``
 *  from the default set; team access is now expressed via ``IntakeForm.team_ids``
 *  and the user-facing "Line of Business" workspace custom field. Issue 2975
 *  dropped ``requester``: it is auto-populated from the creating user at
 *  submission (issue 2947) and shown editable at the top of a submitted request
 *  (issue 2695), so it is no longer placed on page 1. It stays in
 *  ``TIntakeSystemFieldKind`` because existing forms may still carry it. Issue
 *  2948 likewise dropped ``request_owner``: it is offered as a placeable
 *  "workspace fields" palette component instead of an auto-injected page-1
 *  default, and an absent owner auto-assigns the workflow's default owner. Issue
 *  3313 finishes the page-1 lockdown by dropping ``request_reviewer`` too: a
 *  reviewer is set later in the workflow, not filled by the requester on page 1.
 *  It stays in ``TIntakeSystemFieldKind`` because existing forms may still carry
 *  it. ``request_name`` is now the sole auto-injected page-1 default. */
export const DEFAULT_SYSTEM_FIELD_KINDS = [
  "request_name",
] as const satisfies readonly TIntakeSystemFieldKind[];

/** System fields that are OPTIONAL on submission — mirrors the backend's
 *  ``OPTIONAL_DEFAULT_SYSTEM_FIELDS`` (``intake/schema.py``), which is the
 *  authority: it enforces the split whatever a stored definition claims.
 *
 *  One exported set because this rule had drifted into two disagreeing copies.
 *  The builder store computed it as "everything except request_owner"; the
 *  SurveyJS transform carried "everything except request_reviewer". Neither read
 *  the other, and the transform's version put a required asterisk on
 *  ``request_owner`` (#2694, where an absent owner auto-assigns the workflow
 *  default) that a requester could not satisfy. */
export const OPTIONAL_SYSTEM_FIELD_KINDS: ReadonlySet<TIntakeSystemFieldKind> = new Set([
  "request_owner",
  "request_reviewer",
  "target_launch_date",
]);

/** System fields an author may REMOVE from a form, as opposed to the page-1
 *  defaults the builder injects and locks.
 *
 *  Removable is the property every consumer here actually needs, and it is NOT
 *  the same as addable — the two members differ on that:
 *
 *  - ``request_owner`` is removable but can no longer be ADDED. It was a
 *    palette component under #2948, and a July 2026 product decision dropped the
 *    tile, the drag kind and the store adders while deliberately keeping the
 *    field renderable and removable so existing forms kept working. Ownership is
 *    covered by the workflow's Default Request Owner and the top-of-request
 *    owners editor instead.
 *  - ``target_launch_date`` (NG-690) is both: an author adds it from the Global
 *    Fields picker and can take it off again.
 *
 *  What the set gates is the LOCKED treatment, which must not reach either of
 *  them: no padlock badge in place of the row actions, no "the field itself
 *  cannot be removed" notice, and — the one with no symptom —
 *  ``removeElementAtPath`` silently refusing to delete.
 *
 *  Read this instead of spelling ``system_field === "request_owner"`` at each
 *  site. Ten such string compares is how the optional-field rule above ended up
 *  as two disagreeing copies.
 *
 *  Module-private on purpose: every caller goes through
 *  {@link isRemovableSystemField}, which also checks the element's `kind`.
 *  Exporting the bare set invites `SET.has(el.system_field)` at a call site,
 *  which is true for a `datetime` element that happens to carry a stale
 *  `system_field` value. */
const REMOVABLE_SYSTEM_FIELD_KINDS: ReadonlySet<TIntakeSystemFieldKind> = new Set([
  "request_owner",
  "target_launch_date",
]);

/** Whether an element is a system field an author may remove.
 *  See {@link REMOVABLE_SYSTEM_FIELD_KINDS}. */
export function isRemovableSystemField(element: { kind: string; system_field?: string }): boolean {
  return (
    element.kind === "system_field" &&
    REMOVABLE_SYSTEM_FIELD_KINDS.has(element.system_field as TIntakeSystemFieldKind)
  );
}

export interface TIntakeSystemFieldElement extends TIntakeBaseElement {
  kind: "system_field";
  system_field: TIntakeSystemFieldKind;
  label: TIntakeLocalizedText;
  /** Request Title (``request_name``) is editable like a normal input field: it
   *  can carry a placeholder and help-text/description in addition to the label
   *  and tooltip every element already supports (#2693). The other system
   *  fields never author these, but the shape stays uniform and the backend
   *  round-trip persists all three per-locale columns for any element kind. */
  placeholder?: TIntakeLocalizedText;
  description?: TIntakeLocalizedText;
  /** Request Owner (#2948) placed on a post-submission page: when true the
   *  submitted owner *replaces* the request's existing owners; when false (the
   *  default) it is *added* as an additional owner. Only meaningful for a
   *  ``request_owner`` element after the initial submission. */
  replace_current_owner?: boolean;
}

export interface TIntakeChoiceOption {
  value: string;
  label: TIntakeLocalizedText;
  /**
   * Optional per-option hide flag, only meaningful for choice elements that
   * are linked to a workspace global custom field (`bank_field_id` set).
   * When `true`, the option is omitted from the requester submission render
   * but is preserved in the form definition and still surfaces in review
   * screens so historic submissions stay readable (PRD §2.5.b).
   */
  hidden?: boolean;
}

interface TIntakeNamedElement extends TIntakeBaseElement {
  name: string;
  label: TIntakeLocalizedText;
  placeholder?: TIntakeLocalizedText;
  description?: TIntakeLocalizedText;
  show_char_counter?: boolean;
  min_length?: number | null;
  max_length?: number | null;
}

interface TIntakeTextElement extends TIntakeNamedElement {
  kind: "text";
}

interface TIntakeTextareaElement extends TIntakeNamedElement {
  kind: "textarea";
}

interface TIntakeNumberElement extends TIntakeNamedElement {
  kind: "number";
  prefix?: string;
  suffix?: string;
  thousands_separator?: boolean;
  decimal_places?: number | null;
  require_decimal?: boolean;
  min_value?: number | null;
  max_value?: number | null;
}

interface TIntakeCheckboxElement extends TIntakeNamedElement {
  kind: "checkbox";
}

interface TIntakeCheckboxesElement extends TIntakeNamedElement {
  kind: "checkboxes";
  options: TIntakeChoiceOption[];
  inline_layout?: boolean;
  min_checked?: number | null;
  max_checked?: number | null;
  /** Render variant for a multi-select bank field: "checkboxes" (default) or
   *  "dropdown" (a multi-select dropdown / tagbox). Mirrors the custom field's
   *  ``config.display``; snapshotted at add-time like ``options``. */
  display?: "checkboxes" | "dropdown";
}

/**
 * Program field — a singleton, live-sourced multiselect of workspace programs.
 * Options are NOT stored in the definition (resolved at render time from active
 * programs); the value is a list of program-id strings. Configurable for
 * Display / Validation (min/max checked) / Conditional only.
 */
interface TIntakeProgramElement extends TIntakeNamedElement {
  kind: "program";
  min_checked?: number | null;
  max_checked?: number | null;
}

/**
 * Goal field — a singleton, live-sourced picker of the workspace's business
 * goals (NG-618, PRD §3.1.1). Options are NOT stored in the definition
 * (resolved at render time from the workspace's ACTIVE goals); the value is a
 * list of goal-id strings, one-element when single-select.
 *
 * Unlike ``program``, single- vs multi-select is the form author's choice
 * (``multiple``) rather than fixed, so the checked bounds only apply to the
 * multi variant — a bound on a single-select field would attach an
 * answer-count validator to a dropdown that can never hold two answers.
 */
interface TIntakeGoalElement extends TIntakeNamedElement {
  kind: "goal";
  /** Allow more than one goal to be selected. Multi renders as a tagbox,
   *  single as a dropdown; the submitted value is a list either way. */
  multiple?: boolean;
  min_checked?: number | null;
  max_checked?: number | null;
}

/**
 * Request Category field — a singleton, live-sourced single-select dropdown of
 * the workspace's managed Request Category values. Options are NOT stored in
 * the definition (resolved at render time from the active values); the value
 * is a single category-value-id string. Configurable for Display / Validation
 * (required only) / Conditional — no min/max checked (single-select) and no
 * placeholder (parity with ``program``).
 */
interface TIntakeRequestCategoryElement extends TIntakeNamedElement {
  kind: "request_category";
}

/**
 * User List field — a bank-backed multiselect of workspace members. Options
 * are NOT stored in the definition (resolved at render time from the members
 * endpoint); the value is a list of member user-id strings. Unlike ``program``
 * it is not a singleton and is normally bank-linked.
 */
interface TIntakeUserListElement extends TIntakeNamedElement {
  kind: "user_list";
}

/**
 * Kinds that may appear at most once per definition (enforced by the backend
 * schema validator). The builder must not offer duplication for these.
 */
const SINGLETON_ELEMENT_KINDS = ["program", "request_category", "goal"] as const;

/** Derived from the tuple above, so adding a singleton kind there reaches every
 *  consumer that switches on one instead of leaving a silent gap. */
export type TSingletonElementKind = (typeof SINGLETON_ELEMENT_KINDS)[number];

export function isSingletonElementKind(kind: string): boolean {
  return (SINGLETON_ELEMENT_KINDS as readonly string[]).includes(kind);
}

interface TIntakeSelectElement extends TIntakeNamedElement {
  kind: "select";
  options: TIntakeChoiceOption[];
  inline_layout?: boolean;
  /** Render variant for a single-select bank field: "dropdown" (default) or
   *  "radio" (a radio-button group). Mirrors the custom field's
   *  ``config.display``; snapshotted at add-time like ``options``. */
  display?: "dropdown" | "radio";
  /** Allow more than one option to be selected (#2654). When set the dropdown
   *  renders as a multi-select tagbox and the answer is a list of option values.
   *  Ignored when ``display`` is "radio" (single-select by nature). */
  multiple?: boolean;
}

interface TIntakeRadioElement extends TIntakeNamedElement {
  kind: "radio";
  options: TIntakeChoiceOption[];
  inline_layout?: boolean;
}

interface TIntakeEmailElement extends TIntakeNamedElement {
  kind: "email";
}

interface TIntakeUrlElement extends TIntakeNamedElement {
  kind: "url";
  multiple?: boolean;
}

interface TIntakePhoneElement extends TIntakeNamedElement {
  kind: "phone";
  input_mask?: string;
}

interface TIntakeDateTimeElement extends TIntakeNamedElement {
  kind: "datetime";
  format?: string;
  enable_date?: boolean;
  enable_time?: boolean;
  disable_specific_dates?: string[];
  disable_weekends?: boolean;
  disable_weekdays?: boolean;
  twelve_hour?: boolean;
}

interface TIntakeCurrencyElement extends TIntakeNamedElement {
  kind: "currency";
  currency?: string;
}

interface TIntakeFileElement extends TIntakeNamedElement {
  kind: "file";
  /** Allow the requester to upload more than one file into this answer. */
  multiple?: boolean;
  /** Per-element size cap (MB) layered on top of the workspace cap.
   *  Omit for the workspace default. Validated server-side at presign +
   *  finalize. */
  max_size_mb?: number;
  /** MIME-type allow-list. Each entry is either an exact match
   *  (``image/png``) or a family glob (``image/*``), matching the HTML
   *  ``accept`` attribute. Empty / omitted = accept anything the
   *  workspace cap allows. */
  accepted_mime_types?: string[];
}

export type TIntakeExpressionBinaryOp = "add" | "sub" | "mul" | "div";
export type TIntakeExpressionAggregateOp = "sum" | "min" | "max" | "avg";

export type TIntakeExpression =
  | { type: "literal"; value: number }
  | { type: "ref"; field: string }
  | {
      type: "binary";
      op: TIntakeExpressionBinaryOp;
      left: TIntakeExpression;
      right: TIntakeExpression;
    }
  | {
      type: "aggregate";
      op: TIntakeExpressionAggregateOp;
      args: TIntakeExpression[];
    };

export interface TIntakeExpressionElement extends TIntakeBaseElement {
  kind: "expression";
  name: string;
  label: TIntakeLocalizedText;
  expression: TIntakeExpression;
  visible?: boolean; // default true; if false, computed value is hidden from requesters
}

/**
 * Static informational text rendered between fields. Not value-bearing —
 * carries no ``name`` and never shows up in ``response_data``. The body is a
 * localized string so it round-trips in both UI locales.
 */
export interface TIntakeInfoTextElement extends TIntakeBaseElement {
  kind: "info_text";
  content: TIntakeLocalizedText;
}

/**
 * Static formatted content rendered between fields. Like ``info_text`` it is
 * not value-bearing (no ``name``, never in ``response_data``), but its
 * ``content`` carries authored rich-text HTML (sanitized on render).
 */
export interface TIntakeRichTextElement extends TIntakeBaseElement {
  kind: "rich_text";
  content: TIntakeLocalizedText;
}

/**
 * Horizontal rule between sections. No editable properties; ``visible_if``
 * still works so a divider can appear / disappear conditionally.
 */
export interface TIntakeDividerElement extends TIntakeBaseElement {
  kind: "divider";
}

/**
 * Field Group container — wraps a recursive set of child elements under an
 * optional localized title. Layout-only; not value-bearing.
 */
export interface TIntakeFieldGroupElement extends TIntakeBaseElement {
  kind: "field_group";
  /** Optional group heading. */
  label?: TIntakeLocalizedText;
  /**
   * Initial collapsed state in the requester renderer. Defaults to false.
   *
   * INVARIANT: written only by the "Collapsed by default" switch in the
   * properties pane, and read only by `transform-to-survey`. Builder view state
   * must NOT reuse this flag — the canvas fold lives in
   * `intakeStore.builderCollapsedContainerIds`. When the canvas chevron shared
   * it, opening a group to edit its fields switched the author's setting off and
   * autosave persisted it, so the setting never stuck (NG-1087).
   */
  collapsed?: boolean;
  children: TIntakeTreeElement[];
}

/**
 * One column slot inside a ``columns`` container. ``id`` is unique within the
 * parent container; ``children`` is a recursive element list (containers
 * may nest containers).
 */
interface TIntakeColumn {
  id: string;
  children: TIntakeTreeElement[];
}

/**
 * Columns container — 2 to 4 side-by-side column slots, each with its own
 * recursive ``children`` list. Layout-only; not value-bearing.
 */
export interface TIntakeColumnsElement extends TIntakeBaseElement {
  kind: "columns";
  columns: TIntakeColumn[];
}

/**
 * Value-bearing / classic element union — every kind that was supported
 * before PR3. ``TIntakEElement`` is intentionally kept narrow to this union
 * so PR3 doesn't require touching downstream consumer code; PR4 widens
 * consumers to ``TIntakeTreeElement`` when the builder UI learns about the
 * new layout kinds.
 */
export type TIntakeElement =
  | TIntakeSystemFieldElement
  | TIntakeTextElement
  | TIntakeTextareaElement
  | TIntakeNumberElement
  | TIntakeCheckboxElement
  | TIntakeCheckboxesElement
  | TIntakeProgramElement
  | TIntakeGoalElement
  | TIntakeRequestCategoryElement
  | TIntakeUserListElement
  | TIntakeSelectElement
  | TIntakeRadioElement
  | TIntakeEmailElement
  | TIntakeUrlElement
  | TIntakePhoneElement
  | TIntakeDateTimeElement
  | TIntakeCurrencyElement
  | TIntakeFileElement
  | TIntakeExpressionElement;

/**
 * Layout element union — added by PR3. Includes both presentation-only leaves
 * (``info_text``, ``divider``) and recursive containers (``field_group``,
 * ``columns``). None are value-bearing; they never produce a
 * ``response_data`` key.
 */
type TIntakeLayoutElement =
  | TIntakeInfoTextElement
  | TIntakeRichTextElement
  | TIntakeDividerElement
  | TIntakeFieldGroupElement
  | TIntakeColumnsElement;

/**
 * Full tree-element union — every kind the backend canonical schema accepts,
 * including containers. Use this anywhere code needs to handle the full
 * recursive shape. ``TIntakePage.elements`` stays typed as ``TIntakeElement[]``
 * for now so existing flat consumers keep compiling; PR4 widens it.
 */
export type TIntakeTreeElement = TIntakeElement | TIntakeLayoutElement;

export interface TIntakePage {
  id: string;
  name: TIntakeLocalizedText;
  internal?: boolean;
  visible_if?: TIntakeCondition;
  /**
   * Tree-shape element list. Container elements carry
   * their own recursive ``children`` / ``columns`` arrays; callers walking the
   * page must recurse via ``iterTree`` / ``getChildrenAtPath`` in
   * ``lib/intake/tree.ts`` rather than assuming a flat list.
   */
  elements: TIntakeTreeElement[];
}

export interface TIntakeDefinition {
  version: 1;
  pages: TIntakePage[];
}

/** Poll payload for an async form-duplication job (#3621). ``form_id`` is the
 *  produced copy once ``succeeded`` (null before); ``error`` is the failure
 *  reason once ``failed`` (null otherwise). */
export interface TIntakeDuplicationJob {
  job_id: string;
  status: "pending" | "running" | "succeeded" | "failed";
  form_id: string | null;
  error: string | null;
}

export interface TIntakeForm {
  id: string;
  workspace: string;
  name: string;
  /** Authored rich-text description (NG-352) — the writable half of the pair. */
  description_html: string;
  /** Derived plain-text mirror of `description_html`, refreshed server-side on
   *  every save. READ-ONLY: writing it does nothing. Use it where plain text is
   *  what's wanted (a bare list cell, a search hit); render `description_html`
   *  wherever the formatting should show. */
  description: string;
  definition: TIntakeDefinition;
  is_active: boolean;
  teams: string[];
  /** The submitting teams' NAMES, ordered by name, parallel to `teams` above.
   *  Server-resolved on purpose (NG-430): the teams list only widens past the
   *  caller's own teams for a workspace-scope `team.read` holder, so resolving
   *  these client-side showed a shorter list on screen than in the CSV export. */
  team_names: string[];
  request_type: string;
  /** Form-level default Request Category — a managed WorkspaceCategoryValue id
   *  (request-scoped) sourced from the Request Category Standard field. Stamped
   *  onto submissions that don't carry a ``request_category`` answer. Null when
   *  unset. */
  default_request_category_value_id: string | null;
  default_request_owner_ids: string[];
  /** Managing Team — the workflow's OWNER (#3144): whose leads may edit it and
   *  who sees the requests submitted through it. Distinct from ``teams``, which
   *  is the *audience* (Submitting Teams). ``null`` is workspace-managed — no
   *  team owns it, so it appears in no team's tab and only admins edit it. The
   *  read side degrades a soft-deleted team back to ``null`` (GH-3197), so the
   *  pair is always a live team or nothing. */
  managing_team: { id: string; name: string } | null;
  /** The same value as an id, for the write side. Absent on responses that
   *  predate #3144. Send it as ``managing_team_id`` on POST/PATCH. */
  managing_team_id?: string | null;
  /** Tri-state (#3144 D7): ``true`` the caller may edit this workflow,
   *  ``false`` view-only, ``null``/absent the endpoint never evaluated it (the
   *  workspace-settings list's contract). Test it with ``=== false`` — a falsy
   *  test would read "never evaluated" as view-only. */
  can_edit?: boolean | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  /** Autosave + publish workflow meta (author view only — non-authors get
   *  the live shape and these fields are absent). ``draft_updated_at`` is
   *  ISO8601 or null; ``published_at`` is the last successful Publish. */
  draft_updated_at?: string | null;
  published_at?: string | null;
  has_unpublished_changes?: boolean;
  /** Live snapshot kept alongside the current working version so the
   *  builder can preview "Revert to last published" without an extra
   *  round-trip. Absent for non-authors. */
  published?: {
    name: string;
    description_html: string;
    description: string;
    definition: TIntakeDefinition;
    teams: string[];
    /** The ownership axis's last-published value (#3144), so Discard-changes can
     *  show what reverting the Managing Team goes back to. Same GH-3197
     *  degradation as the top-level pair: a soft-deleted owner reads as `null`. */
    managing_team_id: string | null;
    managing_team: { id: string; name: string } | null;
    request_type: string;
    default_request_category_value_id: string | null;
    /** The published default owners. Emitted alongside the category since #3643;
     *  the type omitted it, so a diff over this snapshot could not see it. */
    default_request_owner_ids: string[];
  };
}

interface TIntakeSubmissionSubmitter {
  id: string;
  display_name: string;
  avatar_url: string | null;
}

interface TIntakeSubmissionFormDetail {
  id: string;
  name: string;
  definition?: TIntakeDefinition;
}

/** Per-request SLA adherence state, surfaced in the Request Table SLA column
 *  (exploration Flow 3). ``breached`` includes derived past-due rows. */
export type TIntakeSubmissionSlaState = "within" | "paused" | "breached" | "met";

/** SLA adherence snapshot attached to a submission row when an active SLA
 *  matches the request; ``null`` when none does (rendered as an em-dash). The
 *  shape mirrors the submissions-API ``sla`` object (Task 3.2). */
export interface TIntakeSubmissionSla {
  sla_id: string;
  sla_name: string;
  state: TIntakeSubmissionSlaState;
  /** ISO deadline. Use for a date; `days_remaining` is ceil'd and drifts if you derive one from it. Null when none. */
  due_at: string | null;
  /** Business days left before the target (within/paused); null otherwise. */
  days_remaining: number | null;
  /** Business days past the target (breached); null otherwise. */
  days_overdue: number | null;
  /** Final elapsed business days at close (met); null while open. */
  final_elapsed_days: number | null;
  /** The SLA's configured target in days; null when not applicable. */
  target_days: number | null;
}

/** Priority snapshot attached to a submission row when workspace scoring is
 *  enabled and a score has been computed. ``tier`` is null when the score is
 *  below the Low threshold; the whole object is null when scoring is disabled
 *  or the request has not yet been scored (rendered as an em-dash). Mirrors the
 *  submissions-API ``priority`` object (Task 3.1). */
interface TIntakeSubmissionPriority {
  tier: TPriorityTier | null;
  /** The workspace's custom display name for ``tier`` (NG-1861), or null when
   *  ``tier`` is null or the workspace has not set a custom label for it. Read
   *  this FIRST in ``resolveTierLabel`` callers — it is the only tier name a
   *  caller who cannot reach the ``workspace.edit``-gated prioritization config
   *  endpoint (most non-admins) will ever see; the store-based ``tierLabels``
   *  fallback stays for the settings page and any surface a payload hasn't
   *  reached yet. */
  tier_label: string | null;
  score: number;
  /** True when a human has pinned the tier, overriding the computed score. While
   *  overridden the scoring engine never touches this row; the FE renders a pin
   *  affordance and offers "revert to auto". */
  is_overridden: boolean;
  /** True when the request is in a terminal status group (Completed / Cancelled)
   *  and its priority has been snapshotted (#2865). The tier is read-only while
   *  locked: the FE disables the priority select and shows a lock affordance;
   *  the value unlocks when the request leaves the terminal group. */
  is_locked: boolean;
}

export interface TIntakeSubmissionWorkflowReview {
  /** Always true when present — the request's run is paused on a Review step. */
  awaiting_review: boolean;
  /** The Review step's name (for the indicator's tooltip / label context). */
  review_name: string;
  /** Assigned reviewer user ids; the current user being in this set = they can decide. */
  reviewer_ids: string[];
}

export interface TIntakeSubmission {
  id: string;
  identifier: string | null;
  workspace: string;
  form: string;
  form_detail: TIntakeSubmissionFormDetail;
  submitted_by: string;
  submitted_by_detail: TIntakeSubmissionSubmitter;
  /** The typed ``requester`` FK (the person the request is *for*), distinct from
   *  ``submitted_by`` (who clicked submit). Null when unset. Defaults to the
   *  submitter at create time, so the two usually match until the requester is
   *  reassigned or the request is filed on behalf of someone else. Drives the
   *  list "Requested by" column, matching the detail page's Requester field. */
  requester_id: string | null;
  response_data: Record<string, unknown>;
  locale: TIntakeLocale;
  /** PR6: snapshot of the form's ``team_ids`` at submission time. */
  team_ids: string[];
  request_type: string;
  /** The managed Request Category value picked at submission time (NG-421).
   *  Null on a request filed before the managed category existed, or on a form
   *  that doesn't ask for one. This is the id the ``category`` list filter and
   *  the ``group_by=category`` grouping key match on. */
  category_value_id: string | null;
  /** Display label for the Request Category column. Falls back to the legacy
   *  ``request_type_ref.name`` when ``category_value_id`` is null, so a
   *  pre-migration request still reads as something — which is why a row can
   *  show a label here and still not match a filter on the category value of
   *  the same name (the filter is on the id). Null when neither is set. */
  category_value_label: string | null;
  default_request_owner_ids: string[];
  status: string | null;
  status_detail: { id: string; name: string; color: string; group: string } | null;
  /** NG-1863 (part 2): the statuses this request may move to, in the same shape
   *  the Statuses settings list sends (``TRequestStatus``) — so the status
   *  select can mount from THIS field when the settings list itself 403s
   *  (``state_machine.read``, a Workspace Settings permission distinct from
   *  ``request.status.edit``). Detail read only (``null`` on a list row, like
   *  ``workflow_status_ids``); further gated on the caller holding
   *  ``request.status.edit`` on this request — the same verdict
   *  ``can_change_status`` answers. Absent from an older backend during a
   *  deploy gap, same caveat as ``can_edit``. */
  status_options?: TRequestStatus[] | null;
  /** #4173: why L&D turned this request down. Both null/empty unless it is
   *  currently in a declined status; cleared when it leaves one, so a reopened
   *  request never shows a stale reason. `decline_reason_label` is null (with the
   *  note intact) when the reason was deleted from the workspace list. */
  decline_reason_id: string | null;
  decline_reason_label: string | null;
  decline_note: string;
  /** #4286: why a request is currently waitlisted. Detail responses carry the
   *  ordered labels; list responses intentionally keep these arrays empty to
   *  avoid a per-row M2M prefetch.
   *
   *  Optional, not `string[]`: these are detail-only, and a submission reaches
   *  a render from the shared list collection before its detail read resolves,
   *  so the key can be absent entirely rather than empty. Typing it as required
   *  is what let an unguarded `.map` reach production and take the request
   *  detail route down through the error boundary. Treat absent and empty the
   *  same at every read. */
  waitlist_reason_ids?: string[];
  waitlist_reason_labels?: string[];
  waitlist_note: string;
  /** #4286: why a request is currently cancelled. Mirrors decline's nullable FK
   *  and optional note, cleared when the request leaves the cancelled group. */
  cancel_reason_id: string | null;
  cancel_reason_label: string | null;
  cancel_note: string;
  /** #2961: status ids the request's published FormWorkflow configures, so the
   *  status-select only offers workflow-valid targets. ``null`` when the form
   *  has no published workflow, or on a list read (detail-only to keep the list
   *  query O(1)). */
  workflow_status_ids?: string[] | null;
  /** SLA adherence snapshot when an active SLA matches the request; null
   *  otherwise. Drives the optional Request Table SLA column (Flow 3). */
  sla: TIntakeSubmissionSla | null;
  /** Priority snapshot when workspace scoring is enabled and computed; null
   *  otherwise. Drives the Request Table Priority column (Flow 2). */
  priority: TIntakeSubmissionPriority | null;
  /** Pending approval transitions for this request, folded into the row so the
   *  list/board don't fire a per-row ``pending-transitions`` call (#2069).
   *  Almost always empty — populated only while the request awaits approval.
   *  The request-workflow store seeds its pending cache from this on ingest. */
  pending_transitions: TRequestWorkflowPendingTransition[];
  /** #3305: non-null only while the request's FormWorkflow run is paused on a
   *  Review step. Drives a flag-independent "review pending" indicator on the
   *  list + detail for everyone; ``reviewer_ids`` lets the UI say "awaiting your
   *  approval" to an assigned reviewer and a plain "review pending" to others.
   *  The gated inline decision panel remains the approver's action surface.
   *  Optional (like ``workflow_status_ids``) so existing submission fixtures /
   *  hand-built test objects need not set it; the serializer always emits it. */
  workflow_review?: TIntakeSubmissionWorkflowReview | null;
  /** Set when the request is archived (reversible hide); null when active. */
  archived_at: string | null;
  /** #4196: the server's verdict on whether *this* viewer may edit the request,
   *  mirroring the PATCH admit condition (admin / owner / reviewer / submitter /
   *  managing team lead). Detail read only — absent on a list row, and absent
   *  from an older backend during a deploy gap, which is why
   *  ``canEditSubmittedRequest`` still falls back to admin-or-submitter. */
  can_edit?: boolean;
  /** Whether the caller may move this request's status (NG-363). Tri-state like
   *  ``can_edit``: absent/null means the surface supplied no verdict, which the
   *  gate reads as "fall back" rather than as false. */
  can_change_status?: boolean | null;
  /** Whether the caller may change who this request is for (NG-456). Same
   *  tri-state as `can_change_status`: absent means "no verdict supplied", which
   *  the gate reads as "fall back to the workspace grant", not as false. */
  can_change_requester?: boolean | null;
  /** Whether the caller may change this request's owners (NG-2142). Its own
   *  verdict since NG-2142 — until then the owner write rode the requester verb
   *  and the frontend reused `can_change_requester`, which is the affordance
   *  bug the split closes. Same tri-state as `can_change_requester`. */
  can_change_owners?: boolean | null;
  /** NG-1108: whether a pending workflow Review is holding this request's fields
   *  for *this* viewer — true when the run is parked at a Review and the viewer
   *  is not a workspace admin. Title, requester, owners and form answers render
   *  read-only on it; the status control does not (NG-1088). Same tri-state as
   *  `can_change_status`: absent/null means no verdict, read as unlocked. */
  fields_locked_by_review?: boolean | null;
  /** #4228: the server's verdict on whether *this* viewer may plan work from
   *  the request (the convert preflight + create-from-template gate). Drives
   *  the Plan Work tab and its route. Detail read only, same deploy-gap caveat
   *  as ``can_edit`` — see ``canPlanWorkOnRequest``. */
  can_plan_work?: boolean;
  /** #4228: the server's verdict on ``GET .../links/`` — whether this viewer
   *  may read the projects linked to the request. Independent of
   *  ``can_plan_work``: reading the linkage needs no ``project.create``. Detail
   *  read only — see ``canReadRequestLinkedProjects``. */
  can_read_linked_projects?: boolean;
  /** #4228: the server's verdict on the link *writes*
   *  (``POST``/``DELETE .../links/``) — the "Link projects" button and a row's
   *  Unlink action. Mirrors only the request-level ``request.project.link``
   *  gate; the endpoint's per-target ``project.read`` escalation guard cannot be
   *  folded into a request-level verdict before a target is chosen. Detail read
   *  only — see ``canLinkRequestProjects``. */
  can_link_projects?: boolean;
  /** NG-701: whether this viewer gets the Linked Projects tab's Create Project
   *  button. Owner-only on the server (a ``request.owner`` binding AND
   *  ``request.project.create``), deliberately narrower than ``can_plan_work``
   *  — reviewers and admins hold the capability but not the button. No legacy
   *  fallback: absent (list row / older backend) reads as hidden, which is the
   *  safe deploy-skew direction for a brand-new affordance. Detail read only —
   *  see ``linkedProjectsCreateGate``. */
  can_create_project?: boolean;
  /** NG-1865: the server's verdict on whether this viewer may comment on the
   *  request (``request.comment`` at request OR workspace scope — mirrors the
   *  comment POST's own gate). Detail read only, same deploy-gap caveat as
   *  ``can_edit``: absent (list row / older backend) falls back to true, since
   *  before this field existed the endpoint gated only on the coarse
   *  ``request.read`` every reader already holds. */
  can_comment?: boolean;
  /** NG-210: the projects linked to this request, for the list's Linked Projects
   *  column. Empty means either nothing is linked or the viewer lacks
   *  ``request.project.read`` on this request (the same verdict that hides the
   *  detail page's Linked Projects tab) — both render the column's em-dash.
   *  Optional so existing submission fixtures need not set it; ``null`` from a
   *  backend that predates the field, which the cell treats as "no data" rather
   *  than "no links". */
  linked_projects?: TIntakeSubmissionLinkedProject[] | null;
  /** The programs this request is linked to (NG-1631), whether it named them
   *  at intake or was added from a program page (NG-1734). `null` when the
   *  caller serialized a submission without the batched map: a wiring bug, not
   *  "no programs". The cell renders `programs ?? []`, so `null` and `[]` draw
   *  the same em-dash; the distinction is for whoever reads the payload, not a
   *  behaviour difference. Fenced on program read, so an empty list may also
   *  mean "none you may see". */
  programs?: { id: string; name: string }[] | null;
  /** NG-1157: how many catalogued LMS courses or paths might already serve this
   *  request — the reuse-candidate count shown beside the name in the requests
   *  table, so a triager sees "this may already exist" without opening anything.
   *  It is the count of the SAME rows the request's suggestion panel shows: capped
   *  at the panel's limit, with dismissals excluded, so the table cannot promise a
   *  number the panel then contradicts.
   *
   *  `0` means "we looked and found none"; `null` / absent means "nobody asked" —
   *  an older bundle's fixture, or a backend that predates the field — and the cell
   *  renders nothing at all for it rather than a zero it cannot vouch for. */
  lms_reuse_candidate_count?: number | null;
  /** NG-958: the request's own date — when it was SUBMITTED, which the submitter
   *  may correct. Distinct from `created_at`, which records when the row reached
   *  the database and is what every "Submitted" surface used to read. `null` for a
   *  row the backfill has not reached; read it through
   *  `requestSubmittedAt()` (`helpers/request-date.helper`), never directly, so the
   *  `created_at` fallback stays in one place and in step with the backend's.
   *  Optional as well as nullable: the FE and BE deploy in parallel, so this
   *  bundle has a window where it is talking to an API that predates the field. */
  submitted_at?: string | null;
  created_at: string;
  updated_at: string;
}

/** A project linked to a request, projected for the requests table's Linked
 *  Projects column: the name it renders and the id it keys on, nothing else.
 *  The detail page's Linked Projects tab reads the wider ``TLinkedProject``. */
interface TIntakeSubmissionLinkedProject {
  id: string;
  name: string;
}

/** The three submission list views: active hides archived (default), archived
 *  shows only archived, all shows both. */
export type TIntakeSubmissionsArchivedView = "active" | "archived" | "all";

/**
 * A Review decision option in a published workflow snapshot (BE #2254). Picking
 * one routes the request down that option's lane; ``is_default`` marks the
 * option seeded from Accept/Deny defaults.
 */
interface TWorkflowDecisionOption {
  id: string;
  name: string;
  status_id: string | null;
  is_default: boolean;
  position: number;
  /** The reason-gated disposition this decision lands in, if any (#4286). */
  landing_group: TRequestStatusReasonGroup | null;
  /** Whether picking this option lands the request in a declined status, and so
   *  requires a decline reason (#4173). Computed by the backend from the status's
   *  group. Deprecated compatibility window only; new UI must use
   *  ``landing_group`` so waitlist/cancel decisions get the same gate. */
  lands_in_declined: boolean;
}

export type TRequestStatusReasonGroup = "declined" | "waitlisted" | "cancelled";

export type TStatusReasonSelection =
  | { group: "declined"; reasonId: string; note: string }
  | { group: "cancelled"; reasonId: string; note: string }
  | { group: "waitlisted"; reasonIds: string[]; note: string };

/** A selectable disposition reason, from the workspace's per-group lists (#4286). */
export interface TRequestStatusReason {
  id: string;
  label: string;
  group: TRequestStatusReasonGroup;
  system_code: string;
  is_active: boolean;
  order: number;
  workspace_id: string;
}

/** The Review step's typed payload inside a snapshot element (BE #2254). */
interface TWorkflowReviewStep {
  status_id: string | null;
  notification_message: string;
  reviewer_ids: string[];
  decision_options: TWorkflowDecisionOption[];
}

/**
 * A Review snapshot element the runtime pauses a request on (BE #2254). Only the
 * fields the reviewer decision surface reads are typed; the raw snapshot carries
 * more (canvas layout, graph refs) the FE ignores here.
 */
interface TWorkflowReviewElement {
  id: string;
  kind: string;
  review: TWorkflowReviewStep;
}

/**
 * The ``GET .../workflow/current-page/`` payload (BE #2169 + #2254). On a Review
 * ``current_review`` is populated with ``can_decide`` reflecting whether the
 * caller is an assigned reviewer; ``current_page`` is null. Both are null once
 * the run completes / has moved on.
 */
export interface TWorkflowCurrentStage {
  run_id: string;
  submission_id: string;
  completed: boolean;
  current_element_id?: string;
  current_kind?: string | null;
  current_page: unknown | null;
  /** ``current_page`` projected into the legacy ``definition`` the renderer
   *  consumes, so a pending Page can be rendered without the FE reimplementing
   *  the snapshot → definition projection (NG-368). ``null`` on a Review or a
   *  completed run. Typed loosely for the same reason ``current_page`` is: the
   *  consumer narrows it to {@link TIntakeDefinition}. */
  definition: unknown | null;
  current_review: TWorkflowReviewElement | null;
  assignee_user_ids: string[];
  can_submit: boolean;
  /** Whether to offer "Complete this step" (NG-703): `can_submit` AND the run
   *  was created in explicit-step-completion mode. Server-derived from the mode
   *  persisted on the run — never from this client's flag evaluation, which can
   *  differ from the submitter's during a targeted rollout (NG-1010). */
  can_complete_step: boolean;
  /** Why "Complete this step" would be refused right now (NG-2537): one message
   *  per visible required field with no saved value, from the same check the
   *  complete endpoint runs. Optional so a bundle ahead of its backend reads it
   *  as "nothing known to block", and the endpoint's 400 stays the backstop. */
  complete_step_missing_fields?: string[];
  reviewer_user_ids: string[];
  can_decide: boolean;
}

/**
 * The workflow-path requester render surface (GET .../workflow/render/, #2505 /
 * #2524). Served straight from the form's published workflow snapshot when the
 * requester is routed here (the form has a published workflow) instead of the
 * legacy form-detail definition blob.
 *
 * The FE renders ``definition`` — the fixed first Page reconstructed by the BE
 * into the legacy intake ``definition`` the existing requester renderer
 * (``IntakeRenderer``) consumes — so the FE never duplicates the snapshot →
 * definition projection (mirrors the builder Preview, #2514). ``page`` /
 * ``initially_visible_element_ids`` carry the raw snapshot surface for a future
 * page-element runtime renderer; the reused ``IntakeRenderer`` evaluates
 * ``visible_if`` itself, so they are informational here.
 */
export interface TWorkflowRenderSurface {
  form_id: string;
  workflow_id: string | null;
  snapshot_version: number | null;
  page: unknown | null;
  initially_visible_element_ids: string[];
  definition: TIntakeDefinition;
}

/**
 * Result of a reviewer decision (BE #2170) — the updated request plus where the
 * run landed.
 */
export interface TWorkflowDecisionResult {
  request: TIntakeSubmission;
  reached_end: boolean;
  current_element_id: string | null;
}

/** Result of returning a request to its previous step (BE #2254). */
export interface TWorkflowReturnResult {
  request: TIntakeSubmission;
  current_element_id: string | null;
  current_kind: string | null;
}

/** Which of the requester's own pages a workflow-door submission finishes
 *  (NG-703 / NG-1010). `"all"` is today's walk past every requester page;
 *  `"first"` completes only the page being submitted, so the run halts at the
 *  next unconfirmed one until someone clicks "Complete this step". Absent means
 *  `"all"`. Only the workflow doors (`submitWorkflowForm`, `submitRequestDraft`)
 *  read it — the legacy `submitForm` binds no run. */
export type TCompletesRequesterPages = "all" | "first";

export interface TCreateIntakeSubmission {
  response_data: Record<string, unknown>;
  locale?: TIntakeLocale;
  completes_requester_pages?: TCompletesRequesterPages;
}

/** A private in-progress request draft (NG-702). Visible only to the requester
 *  who created it — the backend hard-scopes every read/write to the caller, so
 *  a draft never appears in any shared surface. `request_name` / `form_name`
 *  are denormalized by the API so a drafts list renders without fetching each
 *  form or parsing `response_data`. */
export interface TIntakeRequestDraft {
  id: string;
  form: string;
  form_name: string;
  request_name: string | null;
  response_data: Record<string, unknown>;
  locale: TIntakeLocale;
  created_at: string;
  updated_at: string;
}

/** Body for draft create/PATCH — both fields optional on PATCH; create defaults
 *  an absent `response_data` to an empty draft. */
export interface TSaveIntakeRequestDraft {
  response_data?: Record<string, unknown>;
  locale?: TIntakeLocale;
}

/** The dimensions the submissions table can group by, server-side. Only
 *  single-valued fields are offered — Owner/Team are multi-valued per
 *  submission, so they don't form unambiguous groups. */
export type SubmissionGroupBy = "none" | "status" | "form" | "category";

/** Complete count for one server-side group (independent of the page window).
 *  ``key`` is the group's id (status_id / form_id), or "" for the empty group. */
export interface TIntakeSubmissionGroupStat {
  key: string;
  count: number;
}

/** Cursor envelope for the paginated submissions list endpoint
 *  (matches the shared `BasePaginator` shape used by workspace projects). */
export interface TIntakeSubmissionsPage {
  results: TIntakeSubmission[];
  next_cursor: string;
  prev_cursor: string;
  next_page_results: boolean;
  prev_page_results: boolean;
  total_count: number;
  total_pages: number;
  count: number;
  /** Active grouping dimension ("status" / "form") when grouped; null/absent otherwise. */
  grouped_by?: string | null;
  /** Complete per-group counts (issue 1524) when grouping; null/absent otherwise. */
  extra_stats?: TIntakeSubmissionGroupStat[] | null;
}

export interface TIntakeSubmissionsListQuery {
  q?: string;
  /** Multi-id filter: passed to BE as CSV (``form=id1,id2``). */
  form?: string[];
  /** Multi-id Programs filter (NG-1631), passed to BE as CSV
   *  (``program=id1,id2``). Fenced server-side on PROGRAM read, so an id the
   *  caller cannot see narrows to nothing rather than disclosing a linkage. */
  program?: string[];
  status?: string[];
  requested_by?: string[];
  owner?: string[];
  team?: string[];
  /** Multi-id managed Request Category filter, passed to BE as CSV
   *  (``category=id1,id2``). Matches ``category_value_id`` only — a legacy
   *  request whose label comes from the request-type fallback does not match. */
  category?: string[];
  /** Multi-state SLA filter, passed to BE as CSV (``sla_state=within,met``).
   *  ``breached`` includes derived past-due rows (mutually exclusive states). */
  sla_state?: string[];
  /** Multi-tier priority filter, passed to BE as CSV (``priority_tier=urgent,high``).
   *  Unknown tiers are ignored server-side. */
  priority_tier?: string[];
  /** Inclusive on the calendar day (``YYYY-MM-DD``), narrowing on the request's
   *  own submission date — the same date the list shows and sorts by (NG-958). */
  submitted_after?: string;
  submitted_before?: string;
  updated_after?: string;
  updated_before?: string;
  /** Archive partition: "active" (default, hides archived), "archived", "all". */
  archived?: TIntakeSubmissionsArchivedView;
  cursor?: string;
  per_page?: number;
  /** Whitelisted server-side sort token, e.g. "created_at" or "-status". */
  order_by?: string;
  /** Server-side grouping dimension: "status", "form" or "category". When set,
   *  the list returns windowed groups + complete counts in ``extra_stats``. */
  group_by?: string;
  /** Drop each row's ``form_detail.definition``, keeping its id and name (NG-2424).
   *  For a read that shows the form's name only; a row fetched this way has no
   *  definition until its detail read replaces it. */
  lean?: boolean;
}

export interface TIntakeRequestTypeOption {
  code: string;
  name: string;
}

/** Convert preflight (PRD §5.2 / §5.3) — see the planner in
 *  ``custom_fields/services.py::plan_submission_to_project_carryover``. */
interface TConvertPreflightSharedEntry {
  field_key: string;
  field_label: string;
  request_value: unknown;
  /** What to display instead of `request_value` when the raw value is a choice
   *  slug. Null for every other kind, where the per-kind formatting below is
   *  what a person should read. */
  request_value_label: string | null;
  kind: string;
}

interface TConvertPreflightArchivedField {
  field_key: string;
  field_label: string;
  on_template: boolean;
}

interface TConvertPreflightArchivedOption {
  field_key: string;
  field_label: string;
  option_value: string;
  option_label: string;
}

interface TConvertPreflightTemplateOnlyOption {
  value: string;
  /** Bank fields store option labels as either a string or an {en,fr} dict. */
  label: string | { en?: string; fr?: string } | null;
}

/** Template fields the user can manually populate at convert time. See the
 *  ``template_only_fields`` bucket in the BE planner. */
export interface TConvertPreflightTemplateOnlyEntry {
  field_key: string;
  field_label: string;
  field_kind: string;
  options: TConvertPreflightTemplateOnlyOption[] | null;
  default_value: unknown;
  current_value: unknown;
}

export interface TConvertPreflight {
  shared_fields: TConvertPreflightSharedEntry[];
  extra_fields: TConvertPreflightSharedEntry[];
  template_only_fields: TConvertPreflightTemplateOnlyEntry[];
  archived_blocked: {
    fields: TConvertPreflightArchivedField[];
    options: TConvertPreflightArchivedOption[];
  };
  /** Trimmed ``request_name`` from the source submission's
   *  ``response_data`` — used by the convert modal to pre-fill the
   *  Project name field. ``null`` when the submission had no
   *  ``request_name`` (unusual; default forms ship the field). */
  submission_request_name: string | null;
  /** Program ids selected on the source request, filtered to programs that
   *  still exist (sticky — any status). Pre-fills the convert program picker. */
  linked_program_ids: string[];
  /** Goal ids linked to the source request, filtered to goals still ACTIVE
   *  (NG-618). Pre-fills the convert goal picker. Read from the materialized
   *  link rows, so a goal added or removed after submission is reflected. */
  linked_goal_ids: string[];
}

/** Row shape returned by ``GET .../linkable-projects/`` — the "Link projects"
 *  picker. State is materialized as ``{id, name, color}`` (or ``null`` for
 *  projects without an assigned state). */
export interface TLinkableProject {
  id: string;
  name: string;
  identifier: string;
  state: { id: string; name: string; color: string } | null;
  start_date: string | null;
  end_date: string | null;
}

/** Cursor-paginated envelope returned by ``BasePaginator`` for the picker
 *  endpoint. Mirrors the shape consumed by ``intake.service.listLinkableProjects``. */
export interface TLinkableProjectsPage {
  results: TLinkableProject[];
  next_cursor: string;
  prev_cursor: string;
  next_page_results: boolean;
  prev_page_results: boolean;
  total_count: number;
  total_pages: number;
  count: number;
}

/** Response from ``POST .../links/`` — partitioned by what actually landed
 *  (``linked``) vs. what was already-linked + skipped (``skipped``). */
export interface TLinkProjectsResult {
  linked: string[];
  skipped: string[];
}

interface TIntakeActorDetail {
  id: string;
  display_name: string;
  email?: string;
  avatar_url?: string | null;
  first_name?: string;
  last_name?: string;
}

/** Submission activity row. Mirrors ``TProjectActivity`` so the shared
 *  activity timeline renders both without branching. */
export interface TIntakeSubmissionActivity {
  id: string;
  submission: string;
  actor: string | null;
  actor_detail?: TIntakeActorDetail | null;
  verb: string;
  field?: string | null;
  old_value?: string | null;
  new_value?: string | null;
  comment?: string;
  attachments?: unknown[];
  epoch: number;
  source_data?: Record<string, unknown>;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Form Workflow Builder — Branch conditions (M4 #2093/#2094)
// ---------------------------------------------------------------------------

/**
 * A field offered as a Branch condition source. The ``available-fields`` endpoint
 * serves these — the value-bearing Page field elements (form-authored *and*
 * bank-linked) on Pages that unconditionally precede the Branch (the BE computes
 * precedence, #2343/#2356). ``id`` is the PageElement id a condition stores as its
 * ``controlling_element_id``; ``kind`` travels as its stable ``CustomFieldKind``
 * code so the editor can offer the right operators / value input per field type.
 */
export interface TWorkflowConditionField {
  id: string;
  name: string;
  kind: TCustomFieldKind;
  /** An option-kind field (``select`` / ``multiselect``) carries its own choices
   *  so the condition value input renders a `<Select>` of them instead of free
   *  text (#2180); other kinds omit it. ``value`` is what the condition stores. */
  options?: { value: string; label: string }[];
}

/** Branch condition operator codes (BE ``OPERATOR_CODES``). */
export type TBranchConditionOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "gt"
  | "lt"
  | "gte"
  | "lte";

/** Global combinator applied across a Branch's condition rows. */
export type TBranchConditionLogic = "and" | "or";

/**
 * One condition row. ``id`` is client-supplied and stable so React keys and the
 * BE write survive reorders. ``position`` is *not* carried here — it is the row
 * index, derived when the Branch panel serializes ``conditions`` to the payload.
 *
 * The source field is referenced one of two ways (#2343/#2356): a preceding Page
 * *element* (``controlling_element_id`` — the current model, any form-authored or
 * bank-linked field) or a legacy bank field (``bank_field_id``). Exactly one is
 * non-null; the BE write enforces that. New rows always set
 * ``controlling_element_id`` (the available-fields picker serves element ids).
 */
export interface TBranchCondition {
  id: string;
  controlling_element_id: string | null;
  bank_field_id: string | null;
  operator: TBranchConditionOperator;
  value: string;
}

/** The Branch condition editor's controlled value (the conditions half of the
 *  Branch payload — ``logic`` + the ordered ``conditions`` rows). */
export interface TBranchConditions {
  logic: TBranchConditionLogic;
  conditions: TBranchCondition[];
}

/** Submission comment. Mirrors ``TProjectComment``. */
export interface TIntakeSubmissionComment {
  id: string;
  submission: string;
  actor: string | null;
  parent: string | null;
  comment_html: string;
  comment_json: Record<string, unknown>;
  comment_stripped: string;
  attachments?: unknown[];
  edited_at: string | null;
  external_source: string | null;
  external_id: string | null;
  created_at: string;
  updated_at: string;
}
